import styled, { css } from 'styled-components'
import { Button } from '@stokr/components-library'
import { Body, BodyClip } from 'components/ui/collapsible/Collapsible.styles'
import { stoColor, stoMedia, stoRadius, stoShadow, stoSpace, stoType } from 'constants/stoTheme'

/** The page's tooltip icon is the shared one. */
export { InfoIcon } from 'components/ui/icons/StatInfoIcon'

/**
 * Sections are cards in the rehauled design: white, 1px border/subtle, radius/lg,
 * padding space/6, children separated by space/8. `plain` opts out for sections that
 * sit directly on the page background (the hero).
 */
const MOBILE = stoMedia.belowDesktop
const DESKTOP = stoMedia.desktopUp

export const SectionRoot = styled.section<{
  $variant: 'card' | 'plain'
  $elevated: boolean
  $collapsible: boolean
  $collapsed?: boolean
  $always?: boolean
}>`
  --section-gap: ${stoSpace[8]};
  /* The card's padding, for the toggle to reach over it. */
  --section-pad-y: 0px;
  --section-pad-x: 0px;
  scroll-margin-top: 96px;
  display: flex;
  flex-direction: column;
  gap: ${({ $collapsible }) => ($collapsible ? '0' : 'var(--section-gap)')};
  margin-bottom: ${stoSpace[6]};

  ${({ $variant, $elevated }) =>
    $variant === 'card' &&
    css`
      --section-pad-y: ${stoSpace[6]};
      --section-pad-x: ${stoSpace[6]};
      padding: var(--section-pad-y) var(--section-pad-x);
      background: ${stoColor.surface};
      border: 1px solid ${stoColor.borderSubtle};
      border-radius: ${stoRadius.lg};
      box-shadow: ${$elevated ? stoShadow.xs : 'none'};

      /* Figma 3582:128652: mobile cards are 20 / 16 with 16 under the header. */
      ${MOBILE} {
        --section-gap: ${stoSpace[4]};
        --section-pad-y: ${stoSpace[5]};
        --section-pad-x: ${stoSpace[4]};
      }
    `}

  ${stoMedia.tabletUp} {
    scroll-margin-top: 104px;
  }

  /* Collapsed, the whole card opens it, so it lifts under the pointer as an investment card does. */
  ${({ $variant, $collapsed, $always }) => {
    if ($variant !== 'card' || !$collapsed) return ''
    const lift = css`
      &:hover {
        box-shadow: ${stoShadow.hover};
      }
    `
    return $always
      ? lift
      : css`
          ${MOBILE} {
            ${lift}
          }
        `
  }}
`

/** Figma header block: title, with a rule beneath it at padding-bottom space/5. */
export const SectionHeader = styled.div<{ $collapsed?: boolean; $always?: boolean }>`
  padding-bottom: ${stoSpace[5]};
  border-bottom: 1px solid ${stoColor.borderSubtle};
  transition:
    padding-bottom 300ms,
    border-color 300ms;

  ${({ $collapsed, $always }) =>
    $collapsed &&
    ($always
      ? css`
          padding-bottom: 0;
          border-bottom-color: transparent;
        `
      : css`
          ${MOBILE} {
            padding-bottom: 0;
            border-bottom-color: transparent;
          }
        `)}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/**
 * The title row, the section's toggle. It reaches over the card's padding so the whole header
 * opens it, and while collapsed the whole card; the text stays where the padding put it.
 */
export const SectionToggle = styled.button<{ $collapsed?: boolean; $always?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${stoSpace[4]};
  width: calc(100% + 2 * var(--section-pad-x));
  margin: calc(-1 * var(--section-pad-y)) calc(-1 * var(--section-pad-x)) 0;
  padding: var(--section-pad-y) var(--section-pad-x) 0;
  border-radius: ${stoRadius.lg};

  ${({ $collapsed, $always }) => {
    const reachBottom = css`
      margin-bottom: calc(-1 * var(--section-pad-y));
      padding-bottom: var(--section-pad-y);
    `
    if (!$collapsed) return ''
    return $always
      ? reachBottom
      : css`
          ${MOBILE} {
            ${reachBottom}
          }
        `
  }}

  border: none;
  background: none;
  font: inherit;
  letter-spacing: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
`

/**
 * Collapsible's animated body, always open from desktop up unless `$always`. The overrides use `&&`:
 * the production bundle can emit the base Collapsible rules after these.
 */
export const SectionBody = styled(Body)<{ $always?: boolean }>`
  ${({ $always }) =>
    !$always &&
    css`
      ${DESKTOP} {
        && {
          grid-template-rows: 1fr;
          opacity: 1;
        }
      }
    `}
`

export const SectionBodyClip = styled(BodyClip)<{ $always?: boolean }>`
  ${({ $always }) =>
    !$always &&
    css`
      ${DESKTOP} {
        && {
          overflow: visible;
        }
      }
    `}
`

export const SectionBodyInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--section-gap);
  padding-top: var(--section-gap);
`

export const SectionHeading = styled.h2`
  ${stoType.sectionTitle};
  /* The global h2 rule uppercases; the design does not. */
  text-transform: none;
  margin: 0;
  color: ${stoColor.black};
`

export const SkeletonBlock = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  border-radius: 2px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: sto-skeleton 1.4s ease infinite;

  & + & {
    margin-top: 12px;
  }

  @keyframes sto-skeleton {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/**
 * Figma small outlined button ("Download", "View declaration"): the library black button at
 * the compact size. Disabled is Figma's grey border with the label at full opacity.
 */
export const CompactButton = styled(Button)`
  ${stoType.labelCta};
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: ${stoSpace[2]};
  min-height: auto;
  padding: 6px ${stoSpace[4]};
  white-space: nowrap;

  /* As a link, the global a { color: inherit !important } would beat the library colours. */
  &[href] {
    color: ${stoColor.textPrimary} !important;

    &:hover,
    &:focus,
    &:active {
      color: ${stoColor.buttonLabelOnFill} !important;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      && {
        background-color: transparent;
        border-color: ${stoColor.borderSubtle};
        color: ${stoColor.textPrimary} !important;
        opacity: 1;
      }
    `}
`

/** Restyles the library ReactTable to the Figma tables; the header stays on scroll. */
export const TableFrame = styled.div`
  overflow: hidden;
  background: ${stoColor.surface};
  border: 1px solid ${stoColor.borderSubtle};
  border-radius: ${stoRadius.lg};

  table {
    table-layout: fixed;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: ${stoSpace[3]} ${stoSpace[4]};
    background: ${stoColor.surfaceAlt};
    border: none;
    /* A shadow, not a border: collapsed borders do not stick with the header. */
    box-shadow: inset 0 -1px 0 ${stoColor.borderSubtle};
    color: ${stoColor.textPrimary};
    font-size: 11px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 1.6px;
    text-align: left;
    text-transform: uppercase;
  }

  td {
    padding: ${stoSpace[3]} ${stoSpace[4]};
    border-bottom: 1px solid ${stoColor.borderSubtle};
    color: ${stoColor.textPrimary};
    font-size: 14px;
    line-height: 14px;
    font-variant-numeric: tabular-nums;
  }

  /* The frame's own border closes the table. */
  tbody tr:last-child td {
    border-bottom: none;
  }
`
